import './QuestionForm.less';
import React from 'react';
import CustomForm from "../../../../bootstrap/components/custom-form/CustomForm";
import types, {
    TYPE_CHECKBOXES,
    TYPE_RADIO_BUTTONS,
    TYPE_TEXT_COMPLETION,
    TYPE_WORD_ARRANGEMENT
} from "../../../model-constants/quiz/types";
import __ from "../../../i18n";
import Button from "../../../../bootstrap/components/button/Button";
import createEmptyPiece from "../utils/createEmptyPiece";
import Icon from "../../../../bootstrap/components/icon/Icon";
import {ReactSortable} from "react-sortablejs";
import DeleteButton from "../delete-button/DeleteButton";


export default function QuestionForm({question, setQuestions, setQuestionErrors, pieces, setPieces, setPieceErrors, setSelectedNodeHash}) {

    const questionPieces = pieces.filter(p => p.question_id === question.id);
    questionPieces.sort((a, b) => a.sort_order - b.sort_order);

    const handleQuestionChange = (values, errors) => {
        values.answer_values = [values.answer_value];
        setQuestions(questions => {
            return questions.map(q => {
                if (q.id === question.id) {
                    Object.assign(q, values);
                }
                return q;
            });
        });
        setQuestionErrors(prevErrors => {
            const _errors = {...prevErrors};
            if (Object.keys(errors).length > 0) {
                _errors[question.id] = errors;
            } else {
                delete _errors[question.id];
            }
            return _errors;
        });
    };

    const getPiecesSectionTitle = () => {
        switch (question.type) {
            case TYPE_RADIO_BUTTONS:
            case TYPE_CHECKBOXES:
            case TYPE_TEXT_COMPLETION:
                return __('Options');
            case TYPE_WORD_ARRANGEMENT:
                return __('Words');
        }
    };

    const getPieceAddButtonTitle = () => {
        switch (question.type) {
            case TYPE_RADIO_BUTTONS:
            case TYPE_CHECKBOXES:
            case TYPE_TEXT_COMPLETION:
                return __('Option');
            case TYPE_WORD_ARRANGEMENT:
                return __('Word');
        }
    };

    return <div className="question-form">
        <div className="info-section">
            <CustomForm
                fieldsConfig={{
                    type: {
                        type: 'singleSelect',
                        name: 'Type',
                        data: {
                            options: types
                        },
                        rules: {
                            required: true
                        }
                    },
                    title: {
                        type: 'text',
                        name: __('Title'),
                        rules: {
                            required: true,
                            maxLength: 120
                        }
                    },
                    content: {
                        type: 'richText',
                        name: __('Content'),
                        rules: {
                            required: question.type === TYPE_TEXT_COMPLETION
                        }
                    },
                    score: {
                        type: 'number',
                        name: __('Score'),
                        rules: {
                            required: true,
                            min: 0,
                            max: 100
                        }
                    },
                    answer_value: {
                        type: 'pillbox',
                        name: 'Answer value',
                        rules: {
                            required: true,
                            duplicatable: true,
                            maxTags: question.type === TYPE_RADIO_BUTTONS ? 1 : undefined,
                        }
                    },
                    answer_explanation: {
                        type: 'richText',
                        name: 'Answer explanation'
                    }
                }}
                layoutConfig={[
                    'type',
                    'title',
                    'content',
                    'score',
                    'answer_value',
                    'answer_explanation',
                ]}
                onInitialized={handleQuestionChange}
                onChange={handleQuestionChange}
                defaultValues={{...question, answer_value: question.answer_values[0]}}
            />
        </div>
        <div className="pieces-section">
            <h3>{getPiecesSectionTitle()}</h3>
            <ReactSortable
                className="piece-list"
                list={questionPieces}
                setList={list => {
                    requestAnimationFrame(() => {
                        list.forEach((p, index) => {
                            setPieces(pieces => {
                                return pieces.map(piece => {
                                    if (piece.id === p.id) {
                                        piece.sort_order = index + 1;
                                    }
                                    return piece;
                                });
                            });
                        });
                    });
                }}
                handle=".handle"
                animation={150}
                fallbackOnBody={true}
            >
                {
                    questionPieces
                        .map(piece => {
                            const handlePieceChange = (values, errors) => {
                                setPieces(pieces => {
                                    return pieces.map(p => {
                                        if (p.id === piece.id) {
                                            Object.assign(p, values);
                                        }
                                        return p;
                                    });
                                });
                                setPieceErrors(prevErrors => {
                                    const _pieceErrors = {...prevErrors};
                                    if (Object.keys(errors).length > 0) {
                                        _pieceErrors[piece.id] = errors;
                                    } else {
                                        delete _pieceErrors[piece.id];
                                    }
                                    return _pieceErrors;
                                });
                            };

                            return <div key={piece.id} className="piece-item">
                                <button type="button" className="handle" tabIndex={-1}>
                                    <Icon name="ellipsis-v"/>
                                </button>
                                <CustomForm
                                    defaultValues={piece}
                                    fieldsConfig={{
                                        value: {
                                            type: 'text',
                                            rules: {
                                                required: true,
                                                maxLength: 120
                                            }
                                        }
                                    }}
                                    layoutConfig={['value']}
                                    onInitialized={handlePieceChange}
                                    onChange={handlePieceChange}
                                />
                                <button
                                    type="button"
                                    className="remove-button"
                                    onClick={() => {
                                        setPieces(pieces => pieces.filter(p => p.id !== piece.id));
                                    }}
                                >
                                    <Icon name="times"/>
                                </button>
                            </div>;
                        })
                }
            </ReactSortable>
            <div>
                <Button
                    title={getPieceAddButtonTitle()}
                    appearance="text-neutral"
                    icon="plus"
                    onClick={() => {
                        const newPiece = createEmptyPiece(question.id, questionPieces.length + 1);
                        setPieces(pieces => pieces.concat([newPiece]));
                    }}
                />
            </div>
        </div>
        <DeleteButton
            title={__('Delete this question')}
            handleDelete={() => {
                setPieces(pieces => pieces.filter(p => p.question_id !== question.id));
                setQuestions(questions => questions.filter(t => t.id !== question.id));
                setSelectedNodeHash('');
            }}
        />
    </div>;
}
